import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TagChip } from './TagChip'
import { Icon, I } from './icons'
import { useObjectStoreGeneration } from '../api/status'
import { fileUrlWithStoreGeneration, safeImageUrl } from '../lib/url'
import type { Entry, MergeSource } from '../api/types'

export type NoteEntry = Extract<Entry, { kind: 'note' }>

type Props = Readonly<{
  note: NoteEntry
  onEdit: (id: number) => void
  onMergeWith?: (source: MergeSource, targetId: number) => void
  onDelete: (note: NoteEntry) => void
  onPin: (note: NoteEntry, pinned: boolean) => void
  onOpen: (note: NoteEntry) => void
}>

// Density mirrors LinkCard's densityFor: tall when a cover image is present,
// medium when there's a body snippet to show, short otherwise.
function densityFor(note: NoteEntry): 'tall' | 'medium' | 'short' {
  if (note.cover_url) return 'tall'
  if (note.body_text_snippet) return 'medium'
  return 'short'
}

// A note drag wins over a link drag when both raw values somehow arrive.
function dropMergeSource(noteRaw: string, linkRaw: string): MergeSource | null {
  if (noteRaw) return { kind: 'note', id: Number(noteRaw) }
  if (linkRaw) return { kind: 'link', id: Number(linkRaw) }
  return null
}

export const NoteCard = memo(NoteCardImpl)
NoteCard.displayName = 'NoteCard'

function NoteCardImpl({ note, onEdit, onMergeWith, onDelete, onPin, onOpen }: Props) {
  const { t } = useTranslation()
  const storeGeneration = useObjectStoreGeneration()
  const previewSrc = fileUrlWithStoreGeneration(safeImageUrl(note.cover_url), storeGeneration)
  // A cover whose object is gone must fall back to the note glyph, not leave a
  // hole. The reset on `cover_url` / store generation is what lets a replaced
  // image or a recovered object store try again — without it, one 503 would
  // suppress the cover for the rest of the session.
  const [coverErrored, setCoverErrored] = useState(false)
  useEffect(() => setCoverErrored(false), [note.cover_url, storeGeneration])
  const showCover = !!previewSrc && !coverErrored
  const density = densityFor(note)

  return (
    <article
      className={'fx-card fx-card-' + density + (note.pinned ? ' fx-card-pinned' : '')}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-foldex-note', String(note.id))
        e.dataTransfer.effectAllowed = 'move'
      }}
      onDragOver={(e) => {
        const raw = e.dataTransfer.types
        if (!raw.includes('application/x-foldex-note') && !raw.includes('application/x-foldex-link')) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
      }}
      onDrop={(e) => {
        const source = dropMergeSource(
          e.dataTransfer.getData('application/x-foldex-note'),
          e.dataTransfer.getData('application/x-foldex-link'),
        )
        if (!source?.id) return
        if (source.kind === 'note' && source.id === note.id) return
        e.preventDefault()
        onMergeWith?.(source, note.id)
      }}
    >
      <button
        className={'fx-card-pin-badge' + (note.pinned ? '' : ' fx-card-pin-off')}
        onClick={(e) => {
          e.stopPropagation()
          onPin(note, !note.pinned)
        }}
        aria-label={note.pinned ? t('note_card.unpin') : t('note_card.pin')}
        data-tooltip={note.pinned ? t('note_card.unpin_tooltip') : t('note_card.pin_top_tooltip')}
        data-tooltip-side="left"
      >
        <Icon d={I.pin} size={13} stroke={2} />
      </button>

      {/* Kind discriminator, always visible (not conditional like the pin
          badge) — opposite corner so it never competes for the pin badge's
          right-side slot. */}
      <span
        className="fx-card-note-badge"
        aria-label={t('note_card.note_badge_tooltip')}
        data-tooltip={t('note_card.note_badge_tooltip')}
        data-tooltip-side="right"
      >
        <Icon d={I.note} size={12} stroke={2} />
      </span>

      {showCover && (
        <button type="button" className="fx-preview fx-preview-img" onClick={() => onOpen(note)} aria-label={t('note_card.read_aria', { title: note.title })}>
          <img
            src={previewSrc}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            onError={() => setCoverErrored(true)}
            style={{ width: '100%', height: '100%', objectFit: 'scale-down', display: 'block' }}
          />
        </button>
      )}
      <div className="fx-card-body">
        <header className="fx-card-head">
          <span className="fx-card-note-icon" aria-hidden="true">
            <Icon d={I.note} size={showCover ? 22 : 28} />
          </span>
          <div className="fx-card-head-text">
            <h3 className="fx-card-title">
              <button type="button" className="fx-card-title-link" onClick={() => onEdit(note.id)}>
                {note.title}
              </button>
            </h3>
          </div>
        </header>

        {note.body_text_snippet && <p className="fx-card-desc">{note.body_text_snippet}</p>}

        {note.tags.length > 0 && (
          <div className="fx-card-tags">
            {note.tags.map((tag) => (
              <TagChip key={tag.id} tag={tag} />
            ))}
          </div>
        )}

        <footer className="fx-card-foot">
          <div className="fx-card-meta">
            <span className="fx-meta-stat" data-tooltip={t('note_card.clicks_tooltip')} aria-label={t('note_card.clicks_tooltip')}>
              <Icon d={I.flame} size={13} /> {note.click_count}
            </span>
          </div>

          <div className="fx-card-actions">
            <button
              className="fx-iconbtn"
              data-tooltip={t('note_card.edit_note')}
              data-tooltip-side="top"
              aria-label={t('common.edit')}
              onClick={() => onEdit(note.id)}
            >
              <Icon d={I.pen} size={14} />
            </button>
            <button
              className="fx-iconbtn fx-iconbtn-danger"
              data-tooltip={t('note_card.delete_note')}
              data-tooltip-side="top"
              aria-label={t('common.delete')}
              onClick={() => onDelete(note)}
            >
              <Icon d={I.trash} size={14} />
            </button>
            {/* A BUTTON, not a link: this opens the note in place. It used to
                be an <a target="_blank"> to the PUBLIC /n/ page, so reading
                your own note meant leaving the app and landing on the same
                anonymous view a share link gives a stranger. The public page
                is still one click away, from inside the modal. */}
            <button
              type="button"
              className="fx-openbtn"
              data-tooltip={t('note_card.open_action')}
              data-tooltip-side="top"
              aria-label={t('note_card.read_aria', { title: note.title })}
              onClick={() => onOpen(note)}
            >
              <span className="fx-openbtn-go">{t('note_card.open_action')}</span>
              <Icon d={I.arrowR} size={14} />
            </button>
          </div>
        </footer>
      </div>
    </article>
  )
}
